// toaster.jsx
import React, { useEffect, useState, useMemo } from 'react';

import Configs from '@/Configs';
import { useToastDispatch } from '@/common/uiShellContext';
import { UnifiedIcon } from '@/components/UnifiedIcon';

// Enhanced Toaster Component with multiple toast support
function Toaster(props) {
  const [visible, setVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const { closeToaster, removeToast } = useToastDispatch();

  // Icon mapping for different toast types
  const getToastIcon = useMemo(() => {
    const iconMap = {
      [Configs.successToaster]: { name: 'CheckCircle', color: 'text-white' },
      [Configs.warningToaster]: { name: 'Warning', color: 'text-white' },
      [Configs.errorToaster]: { name: 'XCircle', color: 'text-white' },
      [Configs.infoToaster]: { name: 'Info', color: 'text-white' },
    };
    return iconMap[props.message.type] || { name: 'Info', color: 'text-white' };
  }, [props.message.type]);

  // Enhanced timeout logic
  useEffect(() => {
    if (props.message.persistent) return; // Don't auto-dismiss persistent toasts
    
    // Longer timeout based on message type and length
    let timeout;
    const messageLength = props.message.message?.length || 0;
    
    if (props.message.type === Configs.errorToaster) {
      timeout = 8000; // 8 seconds for errors
    } else if (props.message.type === Configs.warningToaster) {
      timeout = 6000; // 6 seconds for warnings
    } else if (messageLength > 50) {
      timeout = 7000; // 7 seconds for longer messages
    } else {
      timeout = props.timeout || 5000; // 5 seconds default (up from 3 seconds)
    }
    
    const timer = setTimeout(() => {
      setIsClosing(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [props.timeout, props.message.type, props.message.persistent, props.message.message]);

  // When isClosing becomes true, wait for the transition to finish before closing
  useEffect(() => {
    if (isClosing) {
      const closeTimer = setTimeout(() => {
        setVisible(false);
        
        // Handle both single toast and multiple toast systems
        if (props.message.id && removeToast) {
          removeToast(props.message.id);
        } else {
          closeToaster();
        }
        
        if (props?.onClose) props.onClose();
      }, 300); // Reduced from 400ms to match new animation

      return () => clearTimeout(closeTimer);
    }
  }, [isClosing, closeToaster, removeToast, props]);

  const handleClose = () => {
    setIsClosing(true);
  };

  if (!visible) return null;

  // --------------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------------
  return (
    <div
      className={
        (props?.position === 'relative' ? '' : 'fixed top-4 right-4 lg:top-20 lg:right-6 ') +
        'min-w-[320px] max-w-[480px] text-white py-3 px-4 rounded-lg z-[100] shadow-lg backdrop-blur-sm transition-all duration-300 ease-in-out transform ' +
        (isClosing ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100') +
        ' ' +
        props.message.type +
        ' border border-white/20'
      }
      role="alert"
      data-testid="platform-toast"
      aria-live={props.message.type === Configs.errorToaster ? 'assertive' : 'polite'}
      aria-atomic="true"
      style={{ 
        transitionDelay: isClosing ? '0ms' : '100ms',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)'
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <UnifiedIcon 
            name={getToastIcon.name} 
            library="phosphor" 
            className={`w-5 h-5 ${getToastIcon.color}`}
          />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-5">
            {props.message.message}
          </p>
          
          {/* Action button if provided */}
          {props.message.action && (
            <div className="mt-2">
              <button
                onClick={props.message.action.onClick}
                className="text-xs font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-1"
              >
                {props.message.action.label}
              </button>
            </div>
          )}
        </div>
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
          aria-label="Close notification"
        >
          <UnifiedIcon 
            name="X" 
            library="phosphor" 
            className="w-4 h-4 text-white"
          />
        </button>
      </div>
    </div>
  );
}

export default Toaster;
export { Toaster };
